import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useMinisiteSessionAnalytics } from '../analytics/webAnalytics';
import MonthlyLeadersScreen from './minisite/MonthlyLeadersScreen';
import RemoteNameKeyboard from './minisite/RemoteNameKeyboard';
import { fetchMinisiteLeaderboard, submitMinisiteLeaderboard } from '../api/minisiteLeaderboard';
import { qualifiesForLeaderboard } from '../utils/minisiteLeaderboardQualify';
import {
  createInitialState,
  setPendingHumanBet,
  dealNewRound,
  legalActions,
  applyPlayerAction,
  continueFromSummary,
  runAiStep,
  isHeroBankrupt,
  handValue,
  MIN_BET,
  BET_PRESETS,
  BET_STEP,
  HERO_SEAT_INDEX,
  TABLE_ID,
} from '../utils/blackjack';
import { cardToString } from '../utils/poker/cards';
import { amountToChipValues, amountToDenomStacks } from '../utils/blackjack/chipVisual';
import GsRadioChipPng from './GsRadioChipPng';
import './BlackjackGame.css';
import './minisite/MinisiteShared.css';

const SCORE_CAP = 500000;
/** Focus index for אשר הימור (−, +, presets..., confirm). */
const BET_CONFIRM_FOCUS_INDEX = 2 + BET_PRESETS.length;
/** Table reference PNG watermark; tune without hunting CSS. */
const BJ_TABLE_WATERMARK_OPACITY = 0.055;
const BACK_KEYS = new Set(['Escape', 'BrowserBack', 'GoBack']);

const ACTION_ORDER = ['hit', 'stand', 'double', 'split'];
const ACTION_LABEL = {
  hit: 'קלף',
  stand: 'עצור',
  double: 'כפול',
  split: 'פצל',
};

function ChipDisc({ value, className = '', style = {} }) {
  return <GsRadioChipPng value={value} className={`bj-chip-disc ${className}`.trim()} style={style} />;
}

function BetCircleStack({ wager }) {
  const stacks = wager > 0 ? amountToDenomStacks(wager, 10) : [];
  return (
    <div className="bj-bet-circle">
      <div className="bj-bet-stacks-row" aria-hidden={wager <= 0}>
        {stacks.map((st, colIdx) => (
          <div key={`bet-stack-${colIdx}`} className="bj-bet-stack">
            {Array.from({ length: st.count }, (_, i) => (
              <ChipDisc
                key={i}
                value={st.denom}
                className="bj-chip-disc--in-pot"
                style={{ '--stack-i': i }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SeatBankStack({ bankroll }) {
  const stacks = bankroll > 0 ? amountToDenomStacks(Math.max(0, bankroll), 10) : [];
  return (
    <div className="bj-seat-bank" aria-hidden={bankroll <= 0}>
      {stacks.map((st, colIdx) => (
        <div key={`bank-stack-${colIdx}`} className="bj-seat-bank-stack">
          {Array.from({ length: st.count }, (_, i) => (
            <ChipDisc key={i} value={st.denom} style={{ '--stack-i': i }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function CardRow({ cards, hideSecond, areaRef }) {
  return (
    <div className="bj-cards" ref={areaRef}>
      {cards.map((c, i) => (
        <span key={`${i}-${c.rank}-${c.suit}`} className={`bj-card ${hideSecond && i === 1 ? 'bj-card--hole' : ''}`}>
          {hideSecond && i === 1 ? '?' : cardToString(c)}
        </span>
      ))}
    </div>
  );
}

function BlackjackGame() {
  useMinisiteSessionAnalytics();
  const stateRef = useRef(createInitialState());
  const [uiTick, setUiTick] = useState(0);
  const bump = useCallback(() => setUiTick((t) => t + 1), []);
  const prevPhaseRef = useRef('');

  const [screen, setScreen] = useState('leaders');
  const prevScreenRef = useRef('leaders');

  const [phase, setPhase] = useState('running');
  const [deadStep, setDeadStep] = useState('idle');
  const [joinSel, setJoinSel] = useState('no');
  const [nameInput, setNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [betFocus, setBetFocus] = useState(BET_CONFIRM_FOCUS_INDEX);
  const [actionFocus, setActionFocus] = useState(0);

  const dealerAnchorRef = useRef(null);
  const houseBankRef = useRef(null);
  const seatBankElsRef = useRef({});
  const betCircleElsRef = useRef({});
  const cardShoeRef = useRef(null);
  const dealerCardsAreaRef = useRef(null);
  const playerCardAreaElsRef = useRef({});
  const mainFitRef = useRef(null);
  const mainScaleRef = useRef(null);
  const mainTableRef = useRef(null);

  const postDealSyncedRef = useRef(0);
  const postDealAnimCompleteRef = useRef(0);
  const prevWagersRef = useRef(new Map());
  const settleBuiltForRoundRef = useRef(null);

  const [postDealTrigger, setPostDealTrigger] = useState(null);
  const [postDealGeom, setPostDealGeom] = useState(null);
  const [dealCardGeom, setDealCardGeom] = useState(null);
  const [wagerDeltaGeom, setWagerDeltaGeom] = useState(null);
  const [settleTrigger, setSettleTrigger] = useState(null);
  const [settleGeom, setSettleGeom] = useState(null);
  /** After returning to betting, brief table-only beat before showing the orange bet panel again. */
  const [bettingChromeVisible, setBettingChromeVisible] = useState(false);
  const prevPhaseForBetPanelRef = useRef(null);

  const [totalWon, setTotalWon] = useState(0);
  const scoreRef = useRef(0);
  useEffect(() => {
    scoreRef.current = totalWon;
  }, [totalWon]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    /** Stock Android embedded WebView — needs px-locked shell; desktop browsers stay fluid (resize). */
    const usePixelViewportShell = /Android/i.test(ua) && /;\s*wv\)/i.test(ua);

    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      htmlMinHeight: html.style.minHeight,
      htmlWidth: html.style.width,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      bodyMinHeight: body.style.minHeight,
      bodyWidth: body.style.width,
      bodyMargin: body.style.margin,
      bodyBackground: body.style.background,
      bodyOverscroll: body.style.overscrollBehavior,
      rootHeight: root ? root.style.height : '',
      rootMinHeight: root ? root.style.minHeight : '',
      rootWidth: root ? root.style.width : '',
      rootOverflow: root ? root.style.overflow : '',
      rootPosition: root ? root.style.position : '',
    };

    const bjViewportDebug = () => {
      try {
        return typeof localStorage !== 'undefined' && localStorage.getItem('bjViewportDebug') === '1';
      } catch {
        return false;
      }
    };

    const logBjViewport = (source) => {
      if (!bjViewportDebug()) return;
      const vv = window.visualViewport;
      const pageEl = document.querySelector('.bj-page');
      const br = pageEl?.getBoundingClientRect();
      const cs = getComputedStyle(html);
      console.log(`[BJ viewport] ${source}`, {
        inner: `${window.innerWidth}×${window.innerHeight}`,
        visualViewport: vv ? `${Math.round(vv.width)}×${Math.round(vv.height)}` : null,
        docClient: `${html.clientWidth}×${html.clientHeight}`,
        htmlClassFill: html.classList.contains('bj-blackjack-fill-viewport'),
        pixelShell: usePixelViewportShell,
        cssVars: {
          '--bj-app-width': cs.getPropertyValue('--bj-app-width').trim() || '(unset)',
          '--bj-app-height': cs.getPropertyValue('--bj-app-height').trim() || '(unset)',
          '--bj-vmin-px': cs.getPropertyValue('--bj-vmin-px').trim() || '(unset)',
        },
        bjPageRect: br
          ? { width: Math.round(br.width), height: Math.round(br.height), top: Math.round(br.top), left: Math.round(br.left) }
          : null,
      });
    };

    let coalesceRaf = 0;
    const coalesceReasons = new Set();

    const syncViewport = () => {
      const vv = window.visualViewport;
      const innerH = window.innerHeight > 0 ? window.innerHeight : 0;
      const innerW = window.innerWidth > 0 ? window.innerWidth : 0;
      const vvH = vv && vv.height > 0 ? vv.height : 0;
      const docH = html.clientHeight > 0 ? html.clientHeight : 0;
      /*
       * Do not Math.max() layout sizes with docElement.client* on resize: right after a window
       * shrink, clientWidth/clientHeight can still reflect the previous layout pass, so the shell
       * and --bj-vmin-px would stay “stuck” until something else forced reflow (Chrome desktop).
       */
      let h = vvH > 0 ? vvH : innerH > 0 ? innerH : 0;
      if (usePixelViewportShell) {
        const outerH = typeof window.outerHeight === 'number' && window.outerHeight > 0 ? window.outerHeight : 0;
        if (outerH > h) h = outerH;
      }
      if (h <= 0) h = docH;
      if (h <= 0 && typeof window.screen?.availHeight === 'number') {
        h = window.screen.availHeight;
      }
      if (h <= 0) h = 600;

      const vvW = vv && vv.width > 0 ? vv.width : 0;
      const docW = html.clientWidth > 0 ? html.clientWidth : 0;
      const w = (vvW > 0 ? vvW : innerW > 0 ? innerW : docW) || 0;

      html.style.setProperty('--bj-app-height', `${h}px`);
      if (w > 0) html.style.setProperty('--bj-app-width', `${w}px`);
      if (h > 0 && w > 0) {
        html.style.setProperty('--bj-vmin-px', `${Math.round(Math.min(w, h))}px`);
      }

      body.style.margin = '0';
      body.style.background = '#0a0404';
      if (root) {
        root.style.position = 'relative';
      }

      if (usePixelViewportShell) {
        html.classList.remove('bj-blackjack-fill-viewport');
        html.style.height = `${h}px`;
        html.style.minHeight = `${h}px`;
        html.style.width = w > 0 ? `${w}px` : '100%';
        body.style.height = `${h}px`;
        body.style.minHeight = `${h}px`;
        body.style.width = w > 0 ? `${w}px` : '100%';
        if (root) {
          root.style.height = `${h}px`;
          root.style.minHeight = `${h}px`;
          root.style.width = w > 0 ? `${w}px` : '100%';
        }
      } else {
        /*
         * #root only contains position:absolute .bj-page — percentage heights collapse.
         * Pin .bj-page to the layout viewport via CSS (position:fixed) + this class.
         */
        html.classList.add('bj-blackjack-fill-viewport');
        html.style.removeProperty('height');
        html.style.removeProperty('min-height');
        html.style.width = '100%';
        body.style.removeProperty('height');
        body.style.removeProperty('min-height');
        body.style.width = '100%';
        if (root) {
          root.style.removeProperty('height');
          root.style.removeProperty('min-height');
          root.style.width = '100%';
          root.style.removeProperty('position');
        }
      }
    };

    /** Coalesce window / visualViewport / ResizeObserver: immediate sync + one rAF follow-up per frame. */
    const flushCoalescedViewportSync = () => {
      coalesceRaf = 0;
      const tag = coalesceReasons.size ? [...coalesceReasons].sort().join('+') : 'rAF-followup';
      coalesceReasons.clear();
      syncViewport();
      logBjViewport(tag);
    };

    const requestViewportSync = (reason) => {
      if (reason) coalesceReasons.add(reason);
      if (!coalesceRaf) {
        syncViewport();
        coalesceRaf = requestAnimationFrame(flushCoalescedViewportSync);
      }
    };

    if (usePixelViewportShell) {
      body.classList.add('bj-webview-viewport-lock');
    }
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    if (root) {
      root.style.overflow = 'hidden';
    }

    if (bjViewportDebug()) {
      console.log('[BJ viewport] mount', {
        usePixelViewportShell,
        hasVisualViewport: typeof window.visualViewport !== 'undefined',
        uaTail: ua.length > 96 ? `…${ua.slice(-96)}` : ua,
      });
    }
    syncViewport();
    logBjViewport('mount');

    let lateTimers = [];
    if (usePixelViewportShell) {
      requestAnimationFrame(() => {
        requestAnimationFrame(syncViewport);
      });
      lateTimers = [50, 200, 500, 1000].map((ms) => window.setTimeout(syncViewport, ms));
    }

    const onWindowResize = () => requestViewportSync('window.resize');
    const onOrientationChange = () => requestViewportSync('orientationchange');
    const onPageShow = () => requestViewportSync('pageshow');
    const onVisualViewportResize = () => requestViewportSync('visualViewport.resize');
    const onHtmlResizeObserve = () => requestViewportSync('ResizeObserver(html)');

    window.addEventListener('resize', onWindowResize);
    window.addEventListener('orientationchange', onOrientationChange);
    window.addEventListener('pageshow', onPageShow);
    window.visualViewport?.addEventListener('resize', onVisualViewportResize);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onHtmlResizeObserve);
      resizeObserver.observe(html);
    }

    return () => {
      if (coalesceRaf) cancelAnimationFrame(coalesceRaf);
      coalesceReasons.clear();
      lateTimers.forEach((id) => clearTimeout(id));
      resizeObserver?.disconnect();
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      window.removeEventListener('pageshow', onPageShow);
      window.visualViewport?.removeEventListener('resize', onVisualViewportResize);
      body.classList.remove('bj-webview-viewport-lock');
      html.classList.remove('bj-blackjack-fill-viewport');
      html.style.removeProperty('--bj-app-height');
      html.style.removeProperty('--bj-app-width');
      html.style.removeProperty('--bj-vmin-px');
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      html.style.minHeight = prev.htmlMinHeight;
      html.style.width = prev.htmlWidth;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      body.style.minHeight = prev.bodyMinHeight;
      body.style.width = prev.bodyWidth;
      body.style.margin = prev.bodyMargin;
      body.style.background = prev.bodyBackground;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      if (root) {
        root.style.height = prev.rootHeight;
        root.style.minHeight = prev.rootMinHeight;
        root.style.width = prev.rootWidth;
        root.style.overflow = prev.rootOverflow;
        root.style.position = prev.rootPosition;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (screen !== 'game' || phase === 'dead') return undefined;

    const fit = mainFitRef.current;
    const scaleEl = mainScaleRef.current;
    const table = mainTableRef.current;
    if (!fit || !scaleEl || !table) return undefined;

    let cancelled = false;
    let roFit = null;
    let roTable = null;

    const applyTableFit = () => {
      if (cancelled) return;
      const f = mainFitRef.current;
      const sEl = mainScaleRef.current;
      const tbl = mainTableRef.current;
      if (!f || !sEl || !tbl) return;

      sEl.style.transform = 'none';
      void tbl.offsetWidth;

      const sw = Math.max(tbl.scrollWidth, tbl.clientWidth);
      const sh = Math.max(tbl.scrollHeight, tbl.clientHeight);
      const fw = f.clientWidth;
      const fh = f.clientHeight;
      if (fw < 2 || fh < 2 || sw < 1 || sh < 1) return;

      /*
       * Independent X/Y scale fills `.bj-main-fit` without side letterboxing: uniform scale
       * uses min(fw/sw,fh/sh), so a tall layout on a wide screen shrinks horizontally and
       * wastes width; scale(sx,sy) maps content box to the viewport (small margin).
       */
      const sx = (fw / sw) * 0.998;
      const sy = (fh / sh) * 0.998;
      if (Math.abs(sx - 1) > 0.002 || Math.abs(sy - 1) > 0.002) {
        sEl.style.transform = `scale(${sx}, ${sy})`;
      } else {
        sEl.style.transform = 'none';
      }
    };

    const scheduleApply = () => {
      applyTableFit();
      requestAnimationFrame(() => {
        if (!cancelled) applyTableFit();
      });
    };

    scheduleApply();

    if (typeof ResizeObserver !== 'undefined') {
      roFit = new ResizeObserver(scheduleApply);
      roTable = new ResizeObserver(scheduleApply);
      roFit.observe(fit);
      roTable.observe(table);
    }

    return () => {
      cancelled = true;
      roFit?.disconnect();
      roTable?.disconnect();
    };
  }, [uiTick, screen, phase]);

  const startSession = useCallback(() => {
    stateRef.current = createInitialState();
    setBetFocus(BET_CONFIRM_FOCUS_INDEX);
    setActionFocus(0);
    setPostDealTrigger(null);
    setPostDealGeom(null);
    setDealCardGeom(null);
    setWagerDeltaGeom(null);
    setSettleTrigger(null);
    setSettleGeom(null);
    postDealSyncedRef.current = 0;
    postDealAnimCompleteRef.current = 0;
    prevWagersRef.current = new Map();
    settleBuiltForRoundRef.current = null;
    seatBankElsRef.current = {};
    betCircleElsRef.current = {};
    playerCardAreaElsRef.current = {};
    prevPhaseForBetPanelRef.current = null;
    setBettingChromeVisible(false);
    bump();
  }, [bump]);

  useEffect(() => {
    const st = stateRef.current;
    const ph = st.phase;
    const prev = prevPhaseForBetPanelRef.current;
    if (ph !== 'betting') {
      prevPhaseForBetPanelRef.current = ph;
      return undefined;
    }
    if (prev === 'betting') {
      return undefined;
    }
    prevPhaseForBetPanelRef.current = ph;
    setBettingChromeVisible(false);
    const revealMs = 780;
    const t = window.setTimeout(() => setBettingChromeVisible(true), revealMs);
    return () => window.clearTimeout(t);
  }, [uiTick]);

  const confirmBet = useCallback(() => {
    const st = stateRef.current;
    if (st.phase !== 'betting') return;
    dealNewRound(st, Math.random);
    if (st.message === 'gameOver') {
      setPhase('dead');
      bump();
      return;
    }
    if (st.message) {
      bump();
      return;
    }
    const rid = st.roundId;
    bump();
    setPostDealTrigger({ roundId: rid, key: Date.now() });
  }, [bump]);

  useLayoutEffect(() => {
    if (!postDealTrigger) return;
    const st = stateRef.current;
    const rid = postDealTrigger.roundId;
    const batches = [];
    let delayBase = 0;
    let any = false;
    for (let si = 0; si < st.seats.length; si += 1) {
      const seat = st.seats[si];
      if (!seat.hands.length) continue;
      const amount = seat.hands[0].wager;
      if (amount <= 0) continue;
      const bankEl = seatBankElsRef.current[si];
      const circleEl = betCircleElsRef.current[`${si}-0`];
      if (!bankEl || !circleEl) continue;
      any = true;
      const br = bankEl.getBoundingClientRect();
      const cr = circleEl.getBoundingClientRect();
      const fx = br.left + br.width / 2;
      const fy = br.top + br.height / 2;
      const tx = cr.left + cr.width / 2;
      const ty = cr.top + cr.height / 2;
      const chips = amountToChipValues(amount, 8);
      for (let i = 0; i < chips.length; i += 1) {
        batches.push({
          v: chips[i],
          fx,
          fy,
          tx,
          ty,
          delay: delayBase + i * 42,
        });
      }
      delayBase += chips.length * 42 + 70;
    }

    const participating = st.seats
      .map((se, i) => (se.hands.length > 0 ? i : -1))
      .filter((i) => i >= 0);
    const cardBatches = [];
    const shoeEl = cardShoeRef.current;
    if (shoeEl && participating.length > 0) {
      const sr = shoeEl.getBoundingClientRect();
      const fx = sr.left + sr.width * 0.68;
      const fy = sr.top + sr.height * 0.36;
      let delay = 0;
      const stepDelay = 92;
      const getDealerCenter = () => {
        const el = dealerCardsAreaRef.current;
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      };
      const getPlayerCenter = (si, hi) => {
        const el = playerCardAreaElsRef.current[`${si}-${hi}`];
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      };
      const pushCard = (tx, ty, scaleEnd) => {
        cardBatches.push({ fx, fy, tx, ty, delay, scaleEnd });
        delay += stepDelay;
      };
      for (const si of participating) {
        const p = getPlayerCenter(si, 0);
        if (p) pushCard(p.x - 10, p.y, 1.12);
      }
      const d1 = getDealerCenter();
      if (d1) pushCard(d1.x - 15, d1.y, 1);
      for (const si of participating) {
        const p = getPlayerCenter(si, 0);
        if (p) pushCard(p.x + 12, p.y, 1.14);
      }
      const d2 = getDealerCenter();
      if (d2) pushCard(d2.x + 16, d2.y, 1);
    }

    const hasChipAnim = any && batches.length > 0;
    const hasCardAnim = cardBatches.length > 0;
    if (hasChipAnim) {
      setPostDealGeom({
        key: postDealTrigger.key,
        roundId: rid,
        batches,
        duration: 680,
      });
    }
    if (hasCardAnim) {
      setDealCardGeom({
        key: postDealTrigger.key,
        roundId: rid,
        batches: cardBatches,
        duration: 560,
      });
    }
    if (!hasChipAnim && !hasCardAnim) {
      prevWagersRef.current = new Map();
      st.seats.forEach((seat, si) => {
        seat.hands.forEach((h, hi) => {
          prevWagersRef.current.set(`${si}-${hi}`, h.wager);
        });
      });
      postDealSyncedRef.current = rid;
      postDealAnimCompleteRef.current = rid;
    }
    setPostDealTrigger(null);
  }, [postDealTrigger]);

  useEffect(() => {
    const rid = postDealGeom?.roundId ?? dealCardGeom?.roundId;
    if (rid == null) return undefined;

    const chipMs =
      postDealGeom?.batches?.length > 0
        ? postDealGeom.duration + Math.max(...postDealGeom.batches.map((b) => b.delay), 0) + 90
        : 0;
    const cardMs =
      dealCardGeom?.batches?.length > 0
        ? dealCardGeom.duration + Math.max(...dealCardGeom.batches.map((b) => b.delay), 0) + 100
        : 0;

    const ms = Math.max(chipMs, cardMs);
    if (ms <= 0) return undefined;

    const t = window.setTimeout(() => {
      const st = stateRef.current;
      prevWagersRef.current = new Map();
      st.seats.forEach((seat, si) => {
        seat.hands.forEach((h, hi) => {
          prevWagersRef.current.set(`${si}-${hi}`, h.wager);
        });
      });
      postDealSyncedRef.current = rid;
      postDealAnimCompleteRef.current = rid;
      setPostDealGeom(null);
      setDealCardGeom(null);
    }, ms);
    return () => window.clearTimeout(t);
  }, [postDealGeom, dealCardGeom]);

  useLayoutEffect(() => {
    const st = stateRef.current;
    if (st.phase === 'betting' && st.seats[HERO_SEAT_INDEX].hands.length === 0) return;
    if (postDealSyncedRef.current !== st.roundId) return;
    if (st.phase !== 'playerTurn' && st.phase !== 'dealer') return;

    const batches = [];
    let maxDelay = 0;

    for (let si = 0; si < st.seats.length; si += 1) {
      const seat = st.seats[si];
      for (let hi = 0; hi < seat.hands.length; hi += 1) {
        const h = seat.hands[hi];
        const k = `${si}-${hi}`;
        const w = h.wager;
        const prev = prevWagersRef.current.get(k);
        let delta = 0;
        if (prev === undefined) {
          if (w > 0) delta = w;
        } else if (w > prev) {
          delta = w - prev;
        }
        if (delta <= 0) {
          if (prev === undefined) prevWagersRef.current.set(k, w);
          continue;
        }
        const bankEl = seatBankElsRef.current[si];
        const circleEl = betCircleElsRef.current[k];
        if (!bankEl || !circleEl) {
          prevWagersRef.current.set(k, w);
          continue;
        }
        const br = bankEl.getBoundingClientRect();
        const cr = circleEl.getBoundingClientRect();
        const fx = br.left + br.width / 2;
        const fy = br.top + br.height / 2;
        const tx = cr.left + cr.width / 2;
        const ty = cr.top + cr.height / 2;
        const chips = amountToChipValues(delta, 8);
        for (let i = 0; i < chips.length; i += 1) {
          batches.push({
            v: chips[i],
            fx,
            fy,
            tx,
            ty,
            delay: maxDelay + i * 40,
          });
        }
        maxDelay += chips.length * 40 + 55;
        prevWagersRef.current.set(k, w);
      }
    }
    if (batches.length) {
      setWagerDeltaGeom({ key: Date.now(), batches, duration: 640 });
    }
  }, [uiTick]);

  useEffect(() => {
    if (!wagerDeltaGeom?.batches?.length) return undefined;
    const maxDelay = Math.max(...wagerDeltaGeom.batches.map((b) => b.delay), 0);
    const ms = wagerDeltaGeom.duration + maxDelay + 80;
    const t = window.setTimeout(() => setWagerDeltaGeom(null), ms);
    return () => window.clearTimeout(t);
  }, [wagerDeltaGeom]);

  useEffect(() => {
    const st = stateRef.current;
    if (st.phase !== 'summary') {
      settleBuiltForRoundRef.current = null;
      setSettleGeom(null);
      return;
    }
    if (!st.pendingSettleAnim) return;
    if (postDealAnimCompleteRef.current < st.pendingSettleAnim.roundId) return;
    const rid = st.pendingSettleAnim.roundId;
    if (settleBuiltForRoundRef.current === rid) return;
    settleBuiltForRoundRef.current = rid;
    setSettleTrigger({ ...st.pendingSettleAnim, key: Date.now() });
  }, [uiTick]);

  useLayoutEffect(() => {
    if (!settleTrigger) return;
    const { outcomes } = settleTrigger;
    const batches = [];
    let delayBase = 0;
    for (let oi = 0; oi < outcomes.length; oi += 1) {
      const { seatIndex: si, handIndex: hi, risk, delta } = outcomes[oi];
      const gross = risk + delta;
      const circleEl = betCircleElsRef.current[`${si}-${hi}`];
      if (!circleEl) continue;
      const cr = circleEl.getBoundingClientRect();
      const fx = cr.left + cr.width / 2;
      const fy = cr.top + cr.height / 2;

      if (gross > 0) {
        const bankEl = seatBankElsRef.current[si];
        if (bankEl) {
          const br = bankEl.getBoundingClientRect();
          const tx = br.left + br.width / 2;
          const ty = br.top + br.height / 2;
          const chips = amountToChipValues(gross, 8);
          for (let i = 0; i < chips.length; i += 1) {
            batches.push({
              v: chips[i],
              fx,
              fy,
              tx,
              ty,
              delay: delayBase + i * 42,
            });
          }
          delayBase += chips.length * 42 + 100;
        }
      }
      if (delta < 0) {
        const houseEl = houseBankRef.current;
        if (houseEl) {
          const hr = houseEl.getBoundingClientRect();
          const tx = hr.left + hr.width / 2;
          const ty = hr.top + hr.height / 2;
          const chips = amountToChipValues(-delta, 8);
          for (let i = 0; i < chips.length; i += 1) {
            batches.push({
              v: chips[i],
              fx,
              fy,
              tx,
              ty,
              delay: delayBase + i * 42,
            });
          }
          delayBase += chips.length * 42 + 100;
        }
      }
    }
    setSettleGeom({
      key: settleTrigger.key,
      batches,
      duration: 740,
    });
    setSettleTrigger(null);
  }, [settleTrigger]);

  useEffect(() => {
    if (!settleGeom?.batches?.length) return undefined;
    const maxDelay = Math.max(...settleGeom.batches.map((b) => b.delay), 0);
    const ms = settleGeom.duration + maxDelay + 100;
    const t = window.setTimeout(() => setSettleGeom(null), ms);
    return () => window.clearTimeout(t);
  }, [settleGeom]);

  useEffect(() => {
    const was = prevScreenRef.current;
    prevScreenRef.current = screen;
    if (screen !== 'game') return undefined;
    if (was !== 'leaders') return undefined;
    startSession();
    return undefined;
  }, [screen, startSession]);

  const goToLeaders = useCallback(() => {
    stateRef.current = createInitialState();
    setPhase('running');
    setDeadStep('idle');
    setJoinSel('no');
    setNameInput('');
    setTotalWon(0);
    scoreRef.current = 0;
    setPostDealTrigger(null);
    setPostDealGeom(null);
    setDealCardGeom(null);
    setWagerDeltaGeom(null);
    setSettleTrigger(null);
    setSettleGeom(null);
    postDealSyncedRef.current = 0;
    postDealAnimCompleteRef.current = 0;
    prevWagersRef.current = new Map();
    settleBuiltForRoundRef.current = null;
    seatBankElsRef.current = {};
    betCircleElsRef.current = {};
    playerCardAreaElsRef.current = {};
    prevPhaseForBetPanelRef.current = null;
    setScreen('leaders');
  }, []);

  useEffect(() => {
    const st = stateRef.current;
    if (st.phase === 'summary' && prevPhaseRef.current !== 'summary') {
      const add = Math.max(0, st.heroRoundNetChips);
      if (add > 0) {
        setTotalWon((w) => {
          const nw = Math.min(SCORE_CAP, w + add);
          scoreRef.current = nw;
          return nw;
        });
      }
    }
    prevPhaseRef.current = st.phase;
  }, [uiTick]);

  useEffect(() => {
    if (phase !== 'dead') return undefined;
    let cancelled = false;
    setDeadStep('loading');
    (async () => {
      try {
        const d = await fetchMinisiteLeaderboard('blackjack');
        if (cancelled) return;
        const entries = d.entries || [];
        const sc = Math.min(Math.floor(scoreRef.current), SCORE_CAP);
        const show = qualifiesForLeaderboard('blackjack', entries, sc);
        setDeadStep(show ? 'joinAsk' : 'restartOnly');
      } catch {
        if (!cancelled) setDeadStep('restartOnly');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  useEffect(() => {
    if (phase === 'running') setDeadStep('idle');
  }, [phase]);

  const s = stateRef.current;
  const hero = s.seats[HERO_SEAT_INDEX];
  const humanTurn =
    s.phase === 'playerTurn' &&
    s.activeSeatIndex < s.seats.length &&
    s.seats[s.activeSeatIndex]?.isHuman;

  const actions = (() => {
    const st = stateRef.current;
    const ht =
      st.phase === 'playerTurn' &&
      st.activeSeatIndex < st.seats.length &&
      st.seats[st.activeSeatIndex]?.isHuman;
    if (!ht) return [];
    return ACTION_ORDER.filter((a) =>
      legalActions(st, st.activeSeatIndex, st.activeHandIndex).includes(a)
    );
  })();

  useEffect(() => {
    if (screen !== 'game' || phase !== 'running') return undefined;
    const st = stateRef.current;
    if (st.phase !== 'playerTurn') return undefined;
    const seat = st.seats[st.activeSeatIndex];
    if (!seat || seat.isHuman) return undefined;

    const profile = seat.profile;
    const lo = profile?.tempoMsMin ?? 700;
    const hi = profile?.tempoMsMax ?? 1200;
    const ms = lo + Math.random() * (hi - lo);
    const t = window.setTimeout(() => {
      runAiStep(stateRef.current, Math.random);
      bump();
    }, ms);
    return () => window.clearTimeout(t);
  }, [screen, phase, uiTick, bump]);

  const betItemsCount = 2 + BET_PRESETS.length + 1;

  const phaseLabel = () => {
    switch (s.phase) {
      case 'betting':
        return 'הימור';
      case 'playerTurn':
        return humanTurn ? 'תורך' : 'תור שחקן';
      case 'dealer':
        return 'דילר';
      case 'summary':
        return 'סיכום סיבוב';
      default:
        return '';
    }
  };

  const openBettingFocus = () => {
    setBetFocus(BET_CONFIRM_FOCUS_INDEX);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (deadStep === 'name') return;

      if (phase === 'dead' && deadStep === 'loading') {
        e.preventDefault();
        return;
      }

      if (phase === 'dead' && deadStep === 'joinAsk') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          setJoinSel((v) => (v === 'no' ? 'yes' : 'no'));
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (joinSel === 'yes') {
            setNameInput('');
            setDeadStep('name');
            return;
          }
          goToLeaders();
        }
        return;
      }

      if (phase === 'dead' && deadStep === 'thanks') {
        if (e.key === 'Enter') {
          e.preventDefault();
          goToLeaders();
        }
        return;
      }

      if (screen !== 'game' || phase !== 'running') {
        if (e.key === 'Enter' && phase === 'dead' && deadStep === 'restartOnly') {
          e.preventDefault();
          setTotalWon(0);
          scoreRef.current = 0;
          startSession();
          setPhase('running');
        }
        return;
      }

      const st = stateRef.current;

      if (BACK_KEYS.has(e.key)) {
        if (st.phase === 'betting') {
          e.preventDefault();
          goToLeaders();
        }
        return;
      }

      if (st.phase === 'summary') {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (isHeroBankrupt(st)) {
            setPhase('dead');
            return;
          }
          continueFromSummary(st);
          openBettingFocus();
          bump();
        }
        return;
      }

      if (st.phase === 'betting') {
        if (!bettingChromeVisible) {
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowRight') {
          setBetFocus((f) => (f + 1) % betItemsCount);
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowLeft') {
          setBetFocus((f) => (f - 1 + betItemsCount) % betItemsCount);
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (betFocus === 0) {
            const dec = st.pendingHumanBet > MIN_BET ? BET_STEP : 0;
            setPendingHumanBet(st, st.pendingHumanBet - dec);
            bump();
            return;
          }
          if (betFocus === 1) {
            setPendingHumanBet(st, st.pendingHumanBet + BET_STEP);
            bump();
            return;
          }
          if (betFocus >= 2 && betFocus < 2 + BET_PRESETS.length) {
            const preset = BET_PRESETS[betFocus - 2];
            setPendingHumanBet(st, preset);
            bump();
            return;
          }
          if (betFocus === betItemsCount - 1) {
            confirmBet();
          }
        }
        return;
      }

      if (st.phase === 'playerTurn' && humanTurn && actions.length > 0) {
        if (e.key === 'ArrowRight') {
          setActionFocus((f) => (f + 1) % actions.length);
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowLeft') {
          setActionFocus((f) => (f - 1 + actions.length) % actions.length);
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const act = actions[actionFocus];
          if (act) {
            applyPlayerAction(st, act, Math.random);
            setActionFocus(0);
            bump();
          }
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    screen,
    phase,
    deadStep,
    joinSel,
    goToLeaders,
    startSession,
    betFocus,
    betItemsCount,
    humanTurn,
    actions,
    actionFocus,
    bump,
    confirmBet,
    bettingChromeVisible,
  ]);

  useEffect(() => {
    if (humanTurn && actions.length > 0 && actionFocus >= actions.length) {
      setActionFocus(0);
    }
  }, [humanTurn, actions, actionFocus]);

  const handleNameDone = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await submitMinisiteLeaderboard(
        'blackjack',
        trimmed,
        Math.floor(Math.min(scoreRef.current, SCORE_CAP))
      );
      setDeadStep('thanks');
    } catch {
      setDeadStep('thanks');
    } finally {
      setSubmitting(false);
    }
  }, [nameInput, submitting]);

  const handleNameCancel = useCallback(() => {
    setDeadStep('joinAsk');
    setNameInput('');
  }, []);

  const onLeadersContinue = useCallback(() => {
    setScreen('game');
  }, []);

  if (screen === 'leaders') {
    return (
      <MonthlyLeadersScreen
        game="blackjack"
        scoreLabel="סה״כ זכיות בצ׳יפים"
        onContinue={onLeadersContinue}
      />
    );
  }

  if (phase === 'dead') {
    return (
      <div className="bj-page" dir="rtl">
        <div className="bj-felt" aria-hidden />
        {deadStep === 'name' ? (
          <RemoteNameKeyboard
            value={nameInput}
            onChange={setNameInput}
            onDone={handleNameDone}
            onCancel={handleNameCancel}
          />
        ) : (
          <div className="bj-overlay bj-overlay--scrim">
            <div className="bj-panel bj-dead-panel">
              <div className="bj-dead-title">המאגר נגמר</div>
              <div className="bj-dead-score">
                סה״כ זכיות: {Math.min(totalWon, SCORE_CAP)}
              </div>
              {deadStep === 'loading' && (
                <p className="bj-dead-hint">טוען…</p>
              )}
              {deadStep === 'joinAsk' && (
                <>
                  <p className="bj-dead-hint">להצטרף לטבלת מצטייני החודש?</p>
                  <p className="bj-dead-hint">
                    <span className={joinSel === 'yes' ? 'bj-act--on' : ''}>כן</span>
                    {' · '}
                    <span className={joinSel === 'no' ? 'bj-act--on' : ''}>לא</span>
                  </p>
                  <p className="bj-dead-hint">← → לבחירה · Enter לאישור</p>
                </>
              )}
              {deadStep === 'restartOnly' && (
                <p className="bj-dead-hint">Enter לשחק שוב</p>
              )}
              {deadStep === 'thanks' && (
                <p className="bj-dead-hint">נרשמת בהצלחה! Enter לחזרה למצטיינים</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const dealerShowHole = !s.dealer.holeHidden || s.phase === 'summary';
  const dealerVal = dealerShowHole && s.dealer.cards.length
    ? handValue(s.dealer.cards).total
    : null;

  const tablePotTotal = s.seats.reduce(
    (acc, seat) => acc + seat.hands.reduce((a, h) => a + h.wager, 0),
    0
  );

  const renderCardFlights =
    dealCardGeom && dealCardGeom.batches?.length > 0
      ? dealCardGeom.batches.map((b, i) => (
          <div
            key={`cd-${dealCardGeom.key}-${i}`}
            className="bj-card-fly-wrap"
            style={{
              left: b.fx,
              top: b.fy,
            }}
          >
            <div
              className="bj-card-fly"
              style={{
                '--tx': `${b.tx - b.fx}px`,
                '--ty': `${b.ty - b.fy}px`,
                '--scale-end': String(b.scaleEnd),
                animationDuration: `${dealCardGeom.duration}ms`,
                animationDelay: `${b.delay}ms`,
              }}
            />
          </div>
        ))
      : null;

  const renderFlights = (geom, prefix) =>
    geom && geom.batches?.length > 0
      ? geom.batches.map((b, i) => (
          <div
            key={`${prefix}-${geom.key}-${i}`}
            className="bj-chip-fly-wrap"
            style={{
              left: b.fx,
              top: b.fy,
            }}
          >
            <ChipDisc
              value={b.v}
              className="bj-chip-fly"
              style={{
                '--tx': `${b.tx - b.fx}px`,
                '--ty': `${b.ty - b.fy}px`,
                animationDuration: `${geom.duration}ms`,
                animationDelay: `${b.delay}ms`,
              }}
            />
          </div>
        ))
      : null;

  return (
    <div className="bj-page" dir="rtl">
      <div className="bj-felt" aria-hidden />

      <div className="bj-layout">
        <div className="bj-main-fit" ref={mainFitRef}>
          <div className="bj-main-scale" ref={mainScaleRef}>
            <div
              className={`bj-main ${dealCardGeom ? 'bj-main--dealing' : ''}`.trim()}
              ref={mainTableRef}
            >
        <img
          className="bj-table-ref-watermark"
          src={`${process.env.PUBLIC_URL || ''}/media/bj-table-reference.png`}
          alt=""
          aria-hidden
          style={{ opacity: BJ_TABLE_WATERMARK_OPACITY }}
        />
        <div className="bj-table-rules" aria-hidden>
          <p className="bj-table-rules__line bj-table-rules__line--strong">בלאקג׳ק משלם 3:2</p>
          <p className="bj-table-rules__line bj-table-rules__line--gold">ביטוח משלם 2:1</p>
          <p className="bj-table-rules__line">הדילר עומד על רך 17</p>
        </div>

        <div className="bj-dealer-wrap">
          <div className="bj-dealer" ref={dealerAnchorRef}>
            <div className="bj-dealer-label">דילר{dealerVal != null ? ` · ${dealerVal}` : ''}</div>
            <CardRow
              cards={s.dealer.cards}
              hideSecond={s.dealer.holeHidden && s.phase !== 'summary'}
              areaRef={dealerCardsAreaRef}
            />
          </div>
          <div className="bj-house-bank" ref={houseBankRef}>
            <span className="bj-house-label">בנק הבית</span>
          </div>
        </div>

        <div className="bj-pot-row">
          <span className="bj-pot-label">סה״כ פוט</span>
          <span className="bj-pot-total">{tablePotTotal}</span>
        </div>

        <div className="bj-players-stack">
          {s.phase === 'playerTurn' && humanTurn && actions.length > 0 ? (
            <nav className="bj-actions bj-actions--on-table" aria-label="פעולות" dir="ltr">
              {actions.map((a, i) => (
                <span key={a} className={`bj-act ${i === actionFocus ? 'bj-act--on' : ''}`}>
                  {ACTION_LABEL[a]}
                </span>
              ))}
            </nav>
          ) : null}
          <div className="bj-players-row">
          {[0, 1, 2].map((idx) => {
            const seat = s.seats[idx];
            const isHero = idx === HERO_SEAT_INDEX;
            const has = seat.hands.length > 0;
            return (
              <div
                key={idx}
                className={`bj-seat-col ${isHero ? 'bj-seat-col--human' : ''}`.trim()}
              >
                <div className={`bj-seat-header ${isHero ? 'bj-seat-header--human' : ''}`.trim()}>
                  <span className={`bj-seat-name ${isHero ? 'bj-seat-name--human' : ''}`.trim()}>
                    {seat.displayName}
                  </span>
                  <span className="bj-seat-balance"> · מאזן {seat.bankroll}</span>
                </div>
                <div className="bj-seat-bank-slot">
                  <div
                    ref={(el) => {
                      seatBankElsRef.current[idx] = el;
                    }}
                  >
                    <SeatBankStack bankroll={seat.bankroll} />
                  </div>
                </div>
                <div className="bj-seat-hand-blocks">
                  {!has ? (
                    <span className="bj-seat-total">
                      {isHero ? 'ממתין להימור…' : '—'}
                    </span>
                  ) : (
                    seat.hands.map((h, hi) => {
                      const heroSplitFocus =
                        isHero &&
                        seat.hands.length > 1 &&
                        s.phase === 'playerTurn' &&
                        s.activeSeatIndex === HERO_SEAT_INDEX;
                      if (heroSplitFocus && hi !== s.activeHandIndex) {
                        return null;
                      }
                      const active =
                        s.phase === 'playerTurn' &&
                        s.activeSeatIndex === idx &&
                        s.activeHandIndex === hi;
                      return (
                        <div
                          key={hi}
                          className={`bj-hand-block ${active ? 'bj-hand-block--active' : ''} ${!active && seat.hands.length > 1 ? 'bj-hand-block--dim' : ''}`.trim()}
                        >
                          {seat.hands.length > 1 ? (
                            <span className="bj-seat-name">יד {hi + 1}</span>
                          ) : null}
                          <div
                            className="bj-bet-circle-wrap"
                            ref={(el) => {
                              betCircleElsRef.current[`${idx}-${hi}`] = el;
                            }}
                          >
                            <BetCircleStack wager={h.wager} />
                          </div>
                          <CardRow
                            cards={h.cards}
                            hideSecond={false}
                            areaRef={(el) => {
                              playerCardAreaElsRef.current[`${idx}-${hi}`] = el;
                            }}
                          />
                          {isHero ? (
                            <div className="bj-seat-total">
                              הימור {h.wager}
                              {h.busted ? ' · באסט' : ` · ${handValue(h.cards).total}`}
                              {h.naturalBlackjack ? ' · בלאקג׳ק' : ''}
                            </div>
                          ) : (
                            <span className="bj-seat-total">
                              {h.busted ? 'באסט' : handValue(h.cards).total}
                              {h.naturalBlackjack ? ' · בלאקג׳ק' : ''}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
        </div>
          </div>
        </div>

        <header className="bj-top">
          <span className="bj-top-item">מאזן: {hero.bankroll}</span>
          <span className="bj-top-item">הימור: {s.pendingHumanBet}</span>
          <span className="bj-top-item">{phaseLabel()}</span>
          <span className="bj-top-item">שולחן #{TABLE_ID}</span>
          <span className="bj-top-item">סה״כ זכיות: {Math.min(totalWon, SCORE_CAP)}</span>
        </header>

        <div className="bj-bottom-hud">
          <p className="bj-hint">
            {s.phase === 'betting' && '← → בחירה · Enter לאישור (אשר הימור)'}
            {s.phase === 'playerTurn' && humanTurn && '← → פעולה · Enter'}
            {s.phase === 'summary' && 'Enter להמשך'}
            {' · '}Back מהימור — חזרה למצטיינים
          </p>
        </div>
      </div>

      {s.phase === 'betting' && bettingChromeVisible ? (
        <div className="bj-overlay bj-overlay--table-hud bj-overlay--betting">
          <div className="bj-panel bj-panel--bet">
            <h2 className="bj-panel-title">בחר הימור</h2>
            <div className="bj-bet-row">
              <span className={`bj-chip ${betFocus === 0 ? 'bj-chip--on' : ''}`}>−</span>
              <span className={`bj-chip ${betFocus === 1 ? 'bj-chip--on' : ''}`}>+</span>
              <span className="bj-bet-amt">{s.pendingHumanBet}</span>
            </div>
            <div className="bj-presets">
              {BET_PRESETS.map((p, i) => (
                <span key={p} className={`bj-chip ${betFocus === 2 + i ? 'bj-chip--on' : ''}`}>
                  {p}
                </span>
              ))}
            </div>
            <button
              type="button"
              className={`bj-confirm ${betFocus === betItemsCount - 1 ? 'bj-confirm--on' : ''}`}
              onClick={confirmBet}
            >
              אשר הימור
            </button>
          </div>
        </div>
      ) : null}

      {renderCardFlights}
      {renderFlights(postDealGeom, 'pd')}
      {renderFlights(wagerDeltaGeom, 'wd')}
      {renderFlights(settleGeom, 'st')}

      {s.phase === 'summary' ? (
        <div className="bj-overlay bj-overlay--table-hud">
          <div className="bj-panel">
            <h2 className="bj-panel-title">סיכום</h2>
            <p className="bj-summary-msg">
              סיבוב זה: {s.heroRoundNetChips >= 0 ? '+' : ''}
              {s.heroRoundNetChips} צ׳יפים
            </p>
            <p className="bj-summary-msg">מאזן: {hero.bankroll}</p>
            <p className="bj-dead-hint">Enter להמשך</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default BlackjackGame;
